// screens/marketplace/CoursesOverview.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  BookOpen,
  ReceiptText,
  SlidersHorizontal,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import { PageShell, useTokens } from "./shared";
import { listCourses } from "./coursesApi";
import { listAdminEnrollments } from "./coursesManageApi";

export default function CoursesOverview() {
  const { authFetch, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const t = useTokens();

  const role = user?.role || "AGENT";
  const isAdmin = role === "ADMIN";

  const [counts, setCounts] = useState({
    courses: 0,
    paid: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const courses = await listCourses(authFetch);
      let paid = 0;
      let pending = 0;
      if (isAdmin) {
        try {
          const enrollments = await listAdminEnrollments(authFetch);
          paid = enrollments.filter((e) => e.status === "PAID").length;
          pending = enrollments.filter((e) => e.status === "PENDING").length;
        } catch {
          // Sub-agents/agents may not have admin access; skip counts.
        }
      }
      setCounts({ courses: courses.length, paid, pending });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAdmin, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    {
      key: "catalog",
      icon: BookOpen,
      title: "Course catalog",
      description: isAdmin
        ? "Create, edit, and deactivate courses."
        : "Browse courses available to your store.",
      path: isAdmin ? "/courses/manage/catalog" : "/courses",
      count: `${counts.courses} course${counts.courses === 1 ? "" : "s"}`,
    },
  ];

  if (isAdmin) {
    cards.push(
      {
        key: "enrollments",
        icon: ReceiptText,
        title: "Enrollments",
        description:
          "See buyer contacts and Telegram usernames to accept join requests.",
        path: "/courses/manage/enrollments",
        count: loading
          ? "—"
          : `${counts.paid} paid · ${counts.pending} pending`,
      }
    );
  }

  cards.push({
    key: "pricing",
    icon: SlidersHorizontal,
    title: "Course pricing",
    description:
      role === "SUB_AGENT"
        ? "Set your customer price for each course."
        : "Set customer and sub-agent prices for every course.",
    path: "/courses/manage/pricing",
    count: null,
  });

  return (
    <PageShell
      eyebrow="Courses"
      title="Course management"
      subtitle="Sell digital courses with a Telegram unlock after payment. Manage the catalog, pricing, and enrollments here."
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 14,
        }}
      >
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => navigate(c.path)}
              style={{
                textAlign: "left",
                background: t.surface,
                border: `1px solid ${t.border}`,
                borderRadius: 14,
                padding: 20,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                fontFamily: "inherit",
                transition:
                  "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.borderColor = t.accent;
                e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = t.border;
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: t.subtle,
                    color: t.accent,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={20} />
                </span>
                <ArrowRight size={16} color={t.muted} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: t.heading,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {c.title}
                </div>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 13,
                    color: t.muted,
                    lineHeight: 1.5,
                  }}
                >
                  {c.description}
                </p>
              </div>
              {c.count && (
                <div
                  style={{
                    marginTop: "auto",
                    fontSize: 12,
                    fontWeight: 700,
                    color: t.accent,
                  }}
                >
                  {c.count}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </PageShell>
  );
}